// services/meshyService.js
import meshyDownloadService from './MeshyGLBDownloadService.js';

class MeshyService {
  constructor() {
    // Using Meshy's test/mock API key for development
    this.apiKey = 'msy_ybOhGBfqEMesQi0yOP9GEOUMvwh2TXMSnyDY';
    this.baseURL = 'https://api.meshy.ai/openapi/v1';
  }

  // Convert image file to base64 data URI
  async fileToDataURI(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Convert dimensions to meters (Meshy API expects meters)
  convertToMeters(height, unit) {
    const conversions = {
      'meters': 1,
      'cm': 0.01,
      'mm': 0.001,
      'inches': 0.0254,
      'feet': 0.3048
    };
    return height * (conversions[unit] || 1);
  }

  // Create Image to 3D task (supports 1-4 images)
  async createImageTo3DTask(images, options = {}) {
    try {
      console.log('🚀 Creating Meshy Image-to-3D task...');
      
      let requestBody;
      
      if (images.length === 1) {
        // Single image API
        const imageDataURI = await this.fileToDataURI(images[0]);
        requestBody = {
          image_url: imageDataURI,
          ai_model: options.ai_model || 'meshy-4',
          topology: options.topology || 'triangle',
          target_polycount: options.target_polycount || 30000,
          should_remesh: options.should_remesh !== false,
          should_texture: options.should_texture !== false,
          enable_pbr: options.enable_pbr || false,
          texture_prompt: options.texture_prompt || '',
          moderation: options.moderation || false
        };
      } else {
        // Multi-image API (2-4 images)
        const imageDataURIs = await Promise.all(
          images.map(image => this.fileToDataURI(image))
        );
        requestBody = {
          image_urls: imageDataURIs,
          ai_model: 'meshy-5', // Multi-image only works with meshy-5
          topology: options.topology || 'triangle',
          target_polycount: options.target_polycount || 30000,
          should_remesh: options.should_remesh !== false,
          should_texture: options.should_texture !== false,
          texture_prompt: options.texture_prompt || '',
          moderation: options.moderation || false
        };
      }

      const endpoint = images.length === 1 ? 'image-to-3d' : 'multi-image-to-3d';
      const response = await fetch(`${this.baseURL}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Meshy API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Task created:', result.result);
      return result.result; // Returns task ID
    } catch (error) {
      console.error('❌ Error creating Meshy task:', error);
      throw error;
    }
  }

  // Get task status and results
  async getTask(taskId, isMultiImage = false) {
    try {
      const endpoint = isMultiImage ? 'multi-image-to-3d' : 'image-to-3d';
      const response = await fetch(`${this.baseURL}/${endpoint}/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Meshy API error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error retrieving Meshy task:', error);
      throw error;
    }
  }

  // Poll for task completion
  async waitForTaskCompletion(taskId, isMultiImage = false, onProgress = null, timeout = 300000) {
    const startTime = Date.now();
    const pollInterval = 5000; // Poll every 5 seconds

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          if (Date.now() - startTime > timeout) {
            reject(new Error('Task timeout - generation took too long'));
            return;
          }

          const task = await this.getTask(taskId, isMultiImage);
          
          console.log(`⏳ Progress: ${task.progress}% - Status: ${task.status}`);
          
          if (onProgress) {
            onProgress(task.progress, task.status);
          }

          switch (task.status) {
            case 'SUCCEEDED':
              console.log('✅ Generation completed successfully');
              resolve(task);
              break;
            case 'FAILED':
              reject(new Error(task.task_error?.message || 'Generation failed'));
              break;
            case 'CANCELED':
              reject(new Error('Generation was canceled'));
              break;
            case 'PENDING':
            case 'IN_PROGRESS':
              setTimeout(poll, pollInterval);
              break;
            default:
              reject(new Error(`Unknown task status: ${task.status}`));
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  // Remesh model with custom dimensions
  async remeshModel(originalTaskId, dimensions) {
    try {
      console.log('🔄 Starting remesh with dimensions...');
      
      const requestBody = {
        input_task_id: originalTaskId,
        target_formats: ['glb'],
        topology: 'triangle',
        target_polycount: 30000,
        origin_at: 'bottom'
      };

      // Add resize_height if dimensions are specified
      if (dimensions.height && dimensions.height > 0) {
        const heightInMeters = this.convertToMeters(dimensions.height, dimensions.unit);
        requestBody.resize_height = heightInMeters;
        console.log(`📏 Resizing to ${heightInMeters}m (${dimensions.height} ${dimensions.unit})`);
      }

      const response = await fetch(`${this.baseURL}/remesh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Remesh API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Remesh task created:', result.result);
      return result.result;
    } catch (error) {
      console.error('❌ Error creating remesh task:', error);
      throw error;
    }
  }

  // Get remesh task status
  async getRemeshTask(taskId) {
    try {
      const response = await fetch(`${this.baseURL}/remesh/${taskId}`, {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Remesh API error: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Error retrieving remesh task:', error);
      throw error;
    }
  }

  // Wait for remesh completion
  async waitForRemeshCompletion(taskId, onProgress = null, timeout = 180000) {
    const startTime = Date.now();
    const pollInterval = 3000; // Poll every 3 seconds (remesh is usually faster)

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          if (Date.now() - startTime > timeout) {
            reject(new Error('Remesh timeout'));
            return;
          }

          const task = await this.getRemeshTask(taskId);
          
          console.log(`🔄 Remesh Progress: ${task.progress}% - Status: ${task.status}`);
          
          if (onProgress) {
            onProgress(task.progress, task.status);
          }

          switch (task.status) {
            case 'SUCCEEDED':
              console.log('✅ Remesh completed successfully');
              resolve(task);
              break;
            case 'FAILED':
              reject(new Error('Remesh failed'));
              break;
            case 'PENDING':
            case 'PROCESSING':
            case 'IN_PROGRESS': // Handle this status properly
              setTimeout(poll, pollInterval);
              break;
            default:
              console.warn(`⚠️ Unknown remesh status: ${task.status}, continuing...`);
              setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  // Complete workflow: Generate 3D model from images WITH Firebase upload
  async generateModelFromImages(images, itemName, dimensions = {}, onProgress = null, user = null) {
    try {
      console.log('🚀 Starting complete Meshy workflow with Firebase upload...');
      console.log(`📁 Processing ${images.length} image(s) for: ${itemName}`);
      
      // Step 1: Create initial 3D model
      const isMultiImage = images.length > 1;
      const taskId = await this.createImageTo3DTask(images);
      
      // Step 2: Wait for initial generation
      const originalTask = await this.waitForTaskCompletion(
        taskId,
        isMultiImage,
        (progress, status) => {
          if (onProgress) {
            // If no dimensions specified, this takes 70% of progress
            const finalProgress = (dimensions.height && dimensions.height > 0) ? progress * 0.5 : progress * 0.7;
            onProgress(finalProgress, `Generating 3D model: ${status}`);
          }
        }
      );

      let finalTask = originalTask;

      // Step 3: Remesh with dimensions if specified
      if (dimensions.height && dimensions.height > 0) {
        console.log('📏 Custom dimensions requested...');
        
        if (onProgress) {
          onProgress(50, 'Applying custom dimensions...');
        }
        
        try {
          const remeshTaskId = await this.remeshModel(taskId, dimensions);
          finalTask = await this.waitForRemeshCompletion(
            remeshTaskId,
            (progress, status) => {
              if (onProgress) {
                onProgress(50 + (progress * 0.2), `Resizing model: ${status}`);
              }
            }
          );
        } catch (remeshError) {
          console.warn('⚠️ Remesh failed, using original model:', remeshError.message);
          // Fall back to original model if remesh fails
          finalTask = originalTask;
        }
      }

      // Step 4: 🎯 UPLOAD TO FIREBASE (This is the key fix!)
      if (onProgress) {
        onProgress(70, 'Uploading to Firebase Storage...');
      }

      const glbUrl = finalTask.model_urls?.glb;
      const thumbnailUrl = originalTask.thumbnail_url;

      if (!glbUrl) {
        throw new Error('No GLB file generated');
      }

      // Generate unique item ID for consistent file naming
      const itemId = Date.now().toString();

      console.log('📤 Uploading GLB to Firebase...');
      if (onProgress) {
        onProgress(80, 'Uploading GLB file...');
      }

      // Use meshyDownloadService to upload GLB to Firebase
      const glbUploadResult = await meshyDownloadService.downloadGLBToFirebase(
        glbUrl, 
        itemName, 
        itemId
      );

      if (!glbUploadResult.success) {
        throw new Error(`GLB upload to Firebase failed: ${glbUploadResult.error}`);
      }

      console.log('📤 Uploading thumbnail to Firebase...');
      if (onProgress) {
        onProgress(90, 'Uploading thumbnail...');
      }

      // Upload thumbnail to Firebase if available
      let thumbnailUploadResult = { success: false, firebaseUrl: null };
      if (thumbnailUrl) {
        thumbnailUploadResult = await meshyDownloadService.downloadImageToFirebase(
          thumbnailUrl, 
          itemName, 
          itemId
        );
      }

      if (onProgress) {
        onProgress(100, 'Complete!');
      }

      console.log('🎉 Workflow completed successfully with Firebase URLs!');
      
      // Return Firebase URLs instead of Meshy URLs!
      const result = {
        glbUrl: glbUploadResult.firebaseUrl,        // 🎯 Firebase URL!
        thumbnailUrl: thumbnailUploadResult.success ? thumbnailUploadResult.firebaseUrl : thumbnailUrl,
        taskId: finalTask.id,
        originalTaskId: taskId,
        itemId,
        uploadedToFirebase: true,
        originalMeshyUrls: {
          glb: glbUrl,
          thumbnail: thumbnailUrl
        }
      };
      
      if (dimensions.height && dimensions.height > 0) {
        result.requestedDimensions = dimensions;
        result.dimensionsApplied = true;
      }
      
      return result;

    } catch (error) {
      console.error('❌ Meshy workflow failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
const meshyService = new MeshyService();
export default meshyService;